import React, { useState, useEffect, useCallback } from 'react';
import Joyride, { CallBackProps, STATUS, EVENTS, ACTIONS } from 'react-joyride';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { TourDefinition, TourProgressMap } from '../tours/tourDefinitions';
import { getMyCourses } from '../services/tutorCourseService';

interface Props {
  tour: TourDefinition | null;
  userId: number;
  startStep?: number;
  onComplete: () => void;
  onExit: () => void;
}

const STORAGE_KEY = (userId: number) => `studystride_tour_progress_${userId}`;

function readProgress(userId: number): TourProgressMap {
  try {
    const raw = localStorage.getItem(STORAGE_KEY(userId));
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function writeProgress(userId: number, map: TourProgressMap): void {
  try {
    localStorage.setItem(STORAGE_KEY(userId), JSON.stringify(map));
  } catch {
    // localStorage unavailable — degrade silently
  }
}

const TourRunner: React.FC<Props> = ({ tour, userId, startStep = 0, onComplete, onExit }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [run, setRun] = useState(false);
  const [stepIndex, setStepIndex] = useState(startStep);
  const [firstCourseId, setFirstCourseId] = useState<number | null>(null);
  const [noCourseAvailable, setNoCourseAvailable] = useState(false);

  useEffect(() => {
    if (!tour) {
      setRun(false);
      setNoCourseAvailable(false);
      return;
    }

    setStepIndex(startStep);
    setRun(false);
    setNoCourseAvailable(false);

    const needsCourseId =
      tour.steps.some((s) => s.routeBefore?.includes(':firstCourseId')) ||
      tour.startRoute.includes(':firstCourseId');

    if (needsCourseId) {
      getMyCourses()
        .then((courses) => {
          const courseId = courses[0]?.id ?? null;
          setFirstCourseId(courseId);

          if (!courseId) {
            // No courses yet — show informational dialog instead of broken tour
            setNoCourseAvailable(true);
            return;
          }

          const resolvedStart = tour.startRoute.replace(':firstCourseId', String(courseId));
          if (resolvedStart && location.pathname !== resolvedStart) {
            navigate(resolvedStart);
          }
          setRun(true);
        })
        .catch(() => {
          setFirstCourseId(null);
          setNoCourseAvailable(true);
        });
    } else {
      if (tour.startRoute && location.pathname !== tour.startRoute) {
        navigate(tour.startRoute);
      }
      setRun(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tour?.id, startStep]);

  // Returns null when the route can't be resolved (prevents navigating to a broken path)
  const resolveRoute = useCallback(
    (route: string): string | null => {
      if (!route.includes(':firstCourseId')) return route;
      if (!firstCourseId) return null;
      return route.replace(':firstCourseId', String(firstCourseId));
    },
    [firstCourseId],
  );

  const resolvedSteps = tour ? tour.steps.map((step) => ({ ...step, disableBeacon: true })) : [];

  const handleCallback = useCallback(
    (data: CallBackProps) => {
      const { action, index, status, type } = data;

      // Terminal: tour finished naturally (Finish button on last step)
      if (status === STATUS.FINISHED) {
        if (tour) {
          const map = readProgress(userId);
          writeProgress(userId, {
            ...map,
            [tour.id]: { lastStep: tour.steps.length - 1, completed: true },
          });
        }
        setRun(false);
        onComplete();
        return;
      }

      // Terminal: skip/exit button or overlay close triggered STATUS.SKIPPED
      if (status === STATUS.SKIPPED) {
        setRun(false);
        onExit();
        return;
      }

      // X button on a step — action is CLOSE, status is still RUNNING
      if (action === ACTIONS.CLOSE) {
        setRun(false);
        onExit();
        return;
      }

      // Navigate before a step renders
      if (type === EVENTS.STEP_BEFORE && tour) {
        const step = tour.steps[index];
        if (step?.routeBefore) {
          const resolved = resolveRoute(step.routeBefore);
          if (resolved && location.pathname !== resolved) {
            navigate(resolved);
          }
        }
        return;
      }

      // Normal step advancement (Next / Prev)
      if (type === EVENTS.STEP_AFTER && tour) {
        const nextIndex = action === ACTIONS.PREV ? index - 1 : index + 1;
        const safeNext = Math.max(0, Math.min(nextIndex, tour.steps.length - 1));
        setStepIndex(safeNext);
        const map = readProgress(userId);
        writeProgress(userId, {
          ...map,
          [tour.id]: { lastStep: safeNext, completed: false },
        });
        return;
      }

      // Element not in DOM — skip the step; exit if we're at the end
      if (type === EVENTS.TARGET_NOT_FOUND && tour) {
        const nextIndex = index + 1;
        if (nextIndex >= tour.steps.length) {
          setRun(false);
          onExit();
          return;
        }
        setStepIndex(nextIndex);
      }
    },
    [tour, userId, resolveRoute, location.pathname, navigate, onComplete, onExit],
  );

  // Show a friendly dialog instead of a broken tour when no courses exist
  if (noCourseAvailable && tour) {
    return (
      <Dialog open onClose={onExit} maxWidth="xs" fullWidth>
        <DialogTitle>Course Required</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This tour requires at least one course. Create your first course from{' '}
            <strong>My Courses</strong>, then come back to launch this tour.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={onExit}>
            Got It
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  if (!tour || !run) return null;

  return (
    <Joyride
      steps={resolvedSteps}
      stepIndex={stepIndex}
      run={run}
      continuous
      showProgress
      showSkipButton
      disableScrolling={false}
      disableOverlayClose={false}
      callback={handleCallback}
      locale={{ last: 'Finish', skip: 'Exit Tour', back: 'Previous' }}
      styles={{
        options: {
          primaryColor: '#1976d2',
          zIndex: 10000,
        },
      }}
    />
  );
};

export default TourRunner;
